import { useMutation } from "@tanstack/react-query";
import { addError } from "@wayflyer/blackbox-fe";
import { FullScreenLoader, useToast } from "@wayflyer/flyui";
import type { ConnectionSchemas, CustomerwebSchemas } from "@wayflyer/api-types";
import { useAxios } from "@wayflyer/auth-sdk";
import { type ReactNode, useCallback, useEffect } from "react";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router";
import { useCustomerAppConfig } from "@wayflyer/undercarriage";

import { useConnectionFlowsConfig } from "./config";
import { ConnectionCompletionContext } from "./connection-completion";
import { useCompleteConnectionProps } from "./queries";

type Props = {
  children?: ReactNode;
  onSuccess?: (searchParams: URLSearchParams) => void | Promise<void>;
  onError?: (searchParams: URLSearchParams) => void | Promise<void>;
};

const isCodatConnector = (connector: string): boolean =>
  ["xero", "netsuite", "quickbooks", "codat_accounting", "codat_file_uploads"].includes(connector);

const isMarketingAndAnalyticsConnector = (connector: string): boolean =>
  ["facebook2", "google_ads2", "ga", "tiktok"].includes(connector);

// Bank-connect providers (BANKING category) complete via the separate bankconnect flow, not this
// connector completion endpoint — sending them here 500s (a bank return lacks the connection params
// the endpoint needs). Basiq/TrueLayer redirect back with `?complete-connection`; Plaid uses an
// in-page SDK and never reaches here.
const isBankConnector = (connector: string): boolean => ["basiq", "plaid2", "truelayer"].includes(connector);

/**
 * Shared post-OAuth completion handler. On `?complete-connection=<slug>` it fires the
 * `complete_connection` POST that confirms store connectors (Stripe/Shopify) and starts ingestion.
 * Marketing/analytics (in-panel selector), Codat (server-side) and bank (bankconnect flow) connectors
 * are skipped for that POST; marketing/analytics still report through `onSuccess` when they return
 * on `?connectionSucceed=<slug>`. Mount it around any surface hosting the connection flows so
 * completion behaves identically.
 */
export const ConnectionAuthCallbackHandler = ({ children, onSuccess, onError }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { completeConnectionApiUrl, completeConnectionSubmitApiUrl } = useConnectionFlowsConfig();

  const connectionValue = searchParams.get("complete-connection");
  const oauthError = searchParams.get("oauth-error");
  const connectionId = searchParams.get("connection_id");

  const { data: completeConnectionData, isLoading } = useCompleteConnectionProps(
    customerDomain,
    completeConnectionApiUrl,
    {
      // Codat connectors finalise server-side (no client POST); bank connectors use the bankconnect
      // flow and 500 this endpoint — skip both.
      enabled: !!connectionValue && !isCodatConnector(connectionValue) && !isBankConnector(connectionValue),
      throwOnError: true,
    }
  );

  const {
    mutate: processCompleteConnection,
    isPending,
    isSuccess,
    isError,
  } = useCompleteConnectionMutation({
    onSuccess,
    onError,
    completeConnectionPath: completeConnectionSubmitApiUrl.replace(":customerSlug", customerDomain),
  });

  // Marketing/analytics connectors finish their POST inside the side panel, so the panel reports
  // the completion back up through here rather than through the mutation below.
  const reportConnectionCompleted = useCallback(
    (completedParams: URLSearchParams) => {
      void onSuccess?.(completedParams);
    },
    [onSuccess]
  );

  useEffect(() => {
    // `isError` guards an infinite retry loop: a failed POST leaves the URL unchanged, so without it
    // the effect would re-fire on every re-render.
    if (!connectionValue || !completeConnectionData || isPending || isSuccess || isError) {
      return;
    }

    // Bank connectors complete via the bankconnect flow, not here.
    if (isBankConnector(connectionValue)) {
      return;
    }

    const redirectParams = new URLSearchParams();
    redirectParams.set("platform", connectionValue);

    if (oauthError) {
      redirectParams.set("oauth-error", oauthError);
      setSearchParams(redirectParams);
      onError?.(redirectParams);
      return;
    }

    // Marketing/analytics connectors complete in-panel.
    if (isMarketingAndAnalyticsConnector(connectionValue)) {
      return;
    }

    if (connectionId) {
      redirectParams.set("connection_id", connectionId);
    }
    if (completeConnectionData.firstConnection) {
      redirectParams.set("first-connection", String(completeConnectionData.firstConnection));
    }

    if (completeConnectionData.store || completeConnectionData.firstConnection) {
      processCompleteConnection({ payload: completeConnectionData, redirectParams });
    } else {
      setSearchParams(redirectParams);
    }
  }, [
    connectionValue,
    oauthError,
    connectionId,
    completeConnectionData,
    setSearchParams,
    processCompleteConnection,
    onError,
    isPending,
    isSuccess,
    isError,
  ]);

  if (isLoading || isPending) {
    return <FullScreenLoader />;
  }

  return (
    <ConnectionCompletionContext.Provider value={reportConnectionCompleted}>
      {children}
    </ConnectionCompletionContext.Provider>
  );
};

function useCompleteConnectionMutation({
  onSuccess,
  onError,
  completeConnectionPath,
}: Pick<Props, "onSuccess" | "onError"> & { completeConnectionPath: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const toaster = useToast();
  const intl = useIntl();
  const axiosClient = useAxios();

  return useMutation({
    mutationFn: ({
      payload,
      redirectParams,
    }: {
      payload: ConnectionSchemas.CompleteConnectionPropsSchema;
      redirectParams: URLSearchParams;
    }) =>
      axiosClient.post<CustomerwebSchemas.CompleteCancelEmptySchema>(completeConnectionPath, payload).then(() => {
        const connectionValue = searchParams.get("complete-connection");
        if (connectionValue) {
          redirectParams.set("connectionSucceed", connectionValue);
        }
        setSearchParams(redirectParams);
        onSuccess?.(redirectParams);
      }),
    // Log the raw error for diagnostics; show the user a generic message (never the raw backend one).
    onError: (error: Error) => {
      addError(error);
      toaster.critical(
        intl.formatMessage({
          id: "shared-ui: connection completion failed",
          defaultMessage: "Connection could not be completed. Please try again.",
        })
      );
      onError?.(searchParams);
    },
  });
}
